import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import { deleteSalesImportLineChunksForWeek } from "../features/salesDataImport/services/salesLineChunksService";
import {
  fetchWeeklyImports,
  countTransactionsByImportBatch,
  countTransactionsByPeriodKey,
  countTransactionsByWeekNumber,
  deleteTransactionsByImportBatch,
  deleteTransactionsByPeriodKey,
} from "../features/salesDataImport/services/weeklyImportService";
import { getDisplayWeekRange } from "../features/salesDataImport/utils/weekUtils";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN");
  } catch {
    return iso;
  }
}

export default function SalesDataImportHistoryPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");

  const load = () => {
    setLoading(true);
    fetchWeeklyImports(150)
      .then(setLogs)
      .catch(() => toast.error("Failed to load weekly import history."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    load();
  }, [user]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return logs;
    return logs.filter(
      (l) =>
        String(l.fileName || "").toLowerCase().includes(t) ||
        String(l.uploadedBy || "").toLowerCase().includes(t) ||
        String(l.weekNumber || "").toLowerCase().includes(t) ||
        String(l.fromDate || "").includes(t)
    );
  }, [logs, search]);

  const deleteWeekSlice = async (log) => {
    const pk = log.periodKey || log.weekNumber;
    const n = await deleteTransactionsByPeriodKey(pk);
    if (log.year != null && log.weekOfMonth != null && log.month != null) {
      await deleteSalesImportLineChunksForWeek(log.year, Number(log.month) - 1, log.weekOfMonth);
    }
    return n;
  };

  const handleDeleteBatch = async (log) => {
    setDeletingId(log.id);
    try {
      const batchCount = await countTransactionsByImportBatch(log.id);
      const weekCount = log.weekNumber ? await countTransactionsByWeekNumber(log.weekNumber) : 0;
      const weekLabel = getDisplayWeekRange(log);

      let mode = "batch";
      if (batchCount === 0 && weekCount > 0) {
        const useWeek = window.confirm(
          `No records found for this upload batch.\n\n` +
            `Delete ALL ${weekCount.toLocaleString()} transaction(s) tagged week ${log.weekNumber}?\n` +
            `Calendar week: ${weekLabel}\n\n` +
            `This removes every upload for that week, not just one file. Cannot be undone.`
        );
        if (!useWeek) return;
        mode = "week";
      } else {
        const ok = window.confirm(
          `Delete this upload only?\n\n` +
            `Week: ${log.weekNumber} (${weekLabel})\n` +
            `File: ${log.fileName || "—"}\n` +
            `Uploaded: ${formatDate(log.uploadedAt)} by ${log.uploadedBy || "—"}\n` +
            `Records to delete: ${batchCount.toLocaleString()} (this batch only)\n\n` +
            `Other uploads for the same week are not deleted (${weekCount.toLocaleString()} total in week ${log.weekNumber}).\n\n` +
            `This cannot be undone.`
        );
        if (!ok) return;
      }

      const n =
        mode === "week"
          ? await deleteWeekSlice(log)
          : await deleteTransactionsByImportBatch(log.id);

      toast.success(`Deleted ${n.toLocaleString()} sales record(s).`);
      load();
    } catch (e) {
      toast.error(formatFirebaseError(e), { duration: 8000 });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteEntireWeek = async (log) => {
    const pk = log.periodKey || log.weekNumber;
    if (!pk) return;
    setDeletingId(`week-${pk}`);
    try {
      const weekCount = await countTransactionsByPeriodKey(pk);
      const weekLabel = getDisplayWeekRange(log);
      const ok = window.confirm(
        `Delete ALL transactions and lineChunks for ${pk}?\n\n` +
          `Period: ${weekLabel}\n` +
          `Records to delete: ${weekCount.toLocaleString()} (all uploads for this week)\n\n` +
          `This cannot be undone.`
      );
      if (!ok) return;
      const n = await deleteWeekSlice(log);
      toast.success(`Deleted ${n.toLocaleString()} sales record(s) for ${pk}.`);
      load();
    } catch (e) {
      toast.error(formatFirebaseError(e), { duration: 8000 });
    } finally {
      setDeletingId(null);
    }
  };

  if (!canAccessSalesDataImport(user)) {
    return (
      <div className="p-6 text-center">
        Access denied — Admin or Storekeeper only.
      </div>
    );
  }

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>Weekly Upload History</h1>
          <p>Permanent audit trail — weekly_imports</p>
        </div>
        <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
          {dark ? "Light" : "Dark"}
        </button>
      </header>
      <div className="sdic-body">
        <SalesImportNav active="history" />
        <input
          className="sdic-search"
          placeholder="Search week, user, file, date…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {loading ? (
          <p>Loading…</p>
        ) : (
          <div className="sdic-table-wrap">
            <table className="sdic-table">
              <thead>
                <tr>
                  <th>Upload Date</th>
                  <th>Week</th>
                  <th>Week Range</th>
                  <th>User</th>
                  <th>File</th>
                  <th>Total</th>
                  <th>Uploaded</th>
                  <th>Duplicates</th>
                  <th>Failed</th>
                  <th>Sales Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDate(log.uploadedAt)}</td>
                    <td>{log.weekNumber}</td>
                    <td title={getDisplayWeekRange(log)}>{getDisplayWeekRange(log)}</td>
                    <td>{log.uploadedBy}</td>
                    <td>{log.fileName || "—"}</td>
                    <td>{(log.totalRecords ?? 0).toLocaleString()}</td>
                    <td>{(log.uploadedRecords ?? 0).toLocaleString()}</td>
                    <td>{(log.duplicateRecords ?? 0).toLocaleString()}</td>
                    <td>{(log.failedRecords ?? 0).toLocaleString()}</td>
                    <td>₹ {Number(log.totalSalesAmount || 0).toLocaleString("en-IN")}</td>
                    <td>{log.status || "completed"}</td>
                    <td>
                      <Link
                        to="/sales-data-import"
                        className="sdic-btn sdic-btn-secondary"
                        style={{ padding: "0.25rem 0.5rem" }}
                      >
                        New import
                      </Link>{" "}
                      <button
                        type="button"
                        className="sdic-btn sdic-btn-secondary"
                        style={{ padding: "0.25rem 0.5rem" }}
                        disabled={deletingId === log.id}
                        onClick={() => handleDeleteBatch(log)}
                      >
                        {deletingId === log.id ? "Deleting…" : "Delete upload"}
                      </button>{" "}
                      <button
                        type="button"
                        className="sdic-btn sdic-btn-secondary"
                        style={{ padding: "0.25rem 0.5rem" }}
                        disabled={deletingId === `week-${log.weekNumber}`}
                        onClick={() => handleDeleteEntireWeek(log)}
                        title={`Remove all sales_transactions for ${log.weekNumber}`}
                      >
                        Delete week
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <p style={{ color: "var(--sdic-muted)" }}>No weekly imports yet.</p>
        )}
      </div>
    </div>
  );
}
